import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidResponsesQuery,
  CreateBidResponseInput,
  CreateBidResponseMutation,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateBidQuery } from "../bids/updateBidCache";
import { BidDetails } from "../bids/useBid";
import { bidResponsesKey } from "./useBidResponses";

const CREATE_BID_RESPONSE = graphql(`
  mutation CreateBidResponse($input: CreateBidResponseInput!) {
    createBidResponse(CreateBidResponseInput: $input) {
      id
      bidId
      reference
      title
      section
      description
      weighting
      formattingRequirements
      response
      teamId
      companyId
      stageId
      assignee {
        guid
        firstName
        lastName
      }
      reviewer {
        guid
        firstName
        lastName
      }
      dueDate
      createdAt
      updatedAt
      deletedAt
    }
  }
`);

export function useCreateBidResponse(
  options?: UseMutationOptions<
    CreateBidResponseMutation,
    ClientError,
    { input: CreateBidResponseInput },
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { logEvent } = useTracking();

  const { data, ...rest } = useGraphQlMutation(CREATE_BID_RESPONSE, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const created = data.createBidResponse;

      logEvent(EventNames.bidResponseCreated, {
        "Response title": created.title,
        "Bid GUID": created.bidId,
        "Bid response GUID": created.id,
      });
      options?.onSuccess?.(data, variables, context);

      const bidId = created.bidId;

      if (!bidId) {
        return;
      }

      queryClient.setQueriesData<BidResponsesQuery>(
        {
          queryKey: bidResponsesKey({ resourceId: bidId }),
          exact: false,
        },
        (oldData) => {
          const newBidResponse = created;
          const prevBidResponses = oldData?.bidResponses.items || [];
          const newbidResponses = [newBidResponse, ...prevBidResponses];

          return {
            bidResponses: {
              items: newbidResponses,
              totalCount: newbidResponses.length,
            },
          };
        },
      );

      if (bidId) {
        const { id, stageId } = created;
        const newBidResponse: BidDetails["bidResponses"][0] = { id, stageId };
        updateBidQuery(queryClient, bidId, (bid) => ({
          ...bid,
          bidResponses: [...(bid.bidResponses || []), newBidResponse],
        }));
      }
    },
  });

  return { data: data?.createBidResponse, ...rest };
}
